<template>
  <div>
    <h1> {{ $t('signupIdentity.title') }} </h1>
    <form @submit.prevent="handleSubmit">
      <div class="row">
        <div class="col _12">
          <FormSelect
            id="register-civility"
            v-model="form.civility"
            :label= "$t('signupIdentity.civility.label')"
            required
          >
            <template #default>
              <option :value="null"></option>
              <option value="f"> {{ $t('signupIdentity.civility.mrs') }} </option>
              <option value="m"> {{ $t('signupIdentity.civility.mr') }} </option>
            </template>

            <template v-if="$v.form.civility.$dirty" #error>
              <template v-if="!$v.form.civility.required"> {{ $t('signupIdentity.civility.required') }} </template>
              <template v-else-if="$v.form.civility.$invalid"> {{ $t('signupIdentity.civility.invalid') }} </template>
            </template>
          </FormSelect>
        </div>
      </div>
      <div class="row">
        <div class="col _6, first-name">
          <FormInput
            id="register-first-name"
            v-model="form.firstName"
            :label="$t('signupIdentity.name.label')"
            required
          >
            <template v-if="$v.form.firstName.$dirty" #error>
              <template v-if="!$v.form.firstName.required"> {{ $t('signupIdentity.name.required') }} </template>
              <template v-else-if="$v.form.firstName.$invalid"> {{ $t('signupIdentity.name.invalid') }} </template>
            </template>
          </FormInput>
        </div>
        <div class="col _6">
          <FormInput
            id="register-last-name"
            v-model="form.lastName"
            :label="$t('signupIdentity.lastname.label')"
            required
          >
            <template v-if="$v.form.lastName.$dirty" #error>
              <template v-if="!$v.form.lastName.required"> {{ $t('signupIdentity.lastname.required') }} </template>
              <template v-else-if="$v.form.lastName.$invalid"> {{ $t('signupIdentity.lastname.invalid') }} </template>
            </template>
          </FormInput>
        </div>
      </div>
      <div class="row">
        <div class="col _6, first-name">
          <FormInput
            id="register-birth-date"
            v-model="form.birthDate"
            v-mask="'##/##/####'"
            :label="$t('signupIdentity.birth-date.label')"
            placeholder="__/__/____"
            required
          >
            <template #help> {{ $t('signupIdentity.birth-date.format') }} </template>
            <template v-if="$v.form.birthDate.$dirty" #error>
              <template v-if="!$v.form.birthDate.required"> {{ $t('signupIdentity.birth-date.required') }} </template>
              <template v-else-if="!$v.form.birthDate.over16YearsOld"> {{ $t('signupIdentity.birth-date.age-required') }} </template>
              <template v-else-if="$v.form.birthDate.$invalid"> {{ $t('signupIdentity.birth-date.invalid') }} </template>
            </template>
          </FormInput>
        </div>
        <div class="col _6">
            <FormCountrySelect
              id="register-country"
              v-model="form.country"
              label="Pays"
              required
              @change="$v.form.country.$touch()"
            >
            <template v-if="$v.form.country.$dirty" #error>
                <template v-if="!$v.form.country.required">Le pays est obligatoire</template>
            </template>
            </FormCountrySelect>
        </div>
      </div>

      <DashedLine />

      <div class="row">
        <div class="col _12">
          <FormInput
              id="register-promotional-code"
              v-model="form.discountCode"
              :label="$t('signupIdentity.promotional-code.label')"
          />
        </div>
      </div>

      <FormInputGroup :label="$t('signupIdentity.advice.accept-advice')">
        <FormCheckbox
          :checked="form.acceptToBeContacted === true"
          @change="(form.acceptToBeContacted = $event || null) && $v.form.acceptToBeContacted.$touch()"
        >
          {{ $t('signupIdentity.yes') }}
        </FormCheckbox>

        <FormCheckbox
          :checked="form.acceptToBeContacted === false"
          @change="form.acceptToBeContacted = $event === false && null"
        >
          {{ $t('signupIdentity.no') }}
        </FormCheckbox>

        <template v-if="$v.form.acceptToBeContacted.$dirty" #error>
          <template v-if="!$v.form.acceptToBeContacted.required"> {{ $t('signupIdentity.choice-option') }} </template>
        </template>
      </FormInputGroup>

      <SignupButtons :can-be-submitted="!$v.$invalid" />
    </form>
  </div>
</template>

<script>
  import FormInput from '@/components/Form/FormInput';
  import FormCheckbox from '@/components/Form/FormCheckbox';
  import FormSelect from '@/components/Form/FormSelect.vue';
  import SignupButtons from '@/components/Signup/SignupButtons';
  import DashedLine from '@/components/DashedLine';
  import FormInputGroup from '@/components/Form/FormInputGroup';
  import FormCountrySelect from "@/components/Form/FormCountrySelect.vue";
  import { required } from '@byscripts/vuelidate/lib/validators';
  import { checkDate, civility, over16YearsOld, startsWithLetter, country } from '@/helpers/validation';

  export default {
    name: 'SignupIdentity',
    components: {
      FormCountrySelect,
      FormInputGroup,
      DashedLine,
      SignupButtons,
      FormSelect,
      FormCheckbox,
      FormInput,
    },
    data() {
      return {
        form: {
          civility: this.$store.state.signup.form.civility,
          firstName: this.$store.state.signup.form.firstName,
          lastName: this.$store.state.signup.form.lastName,
          birthDate: this.$store.state.signup.form.birthDate,
          acceptToBeContacted: this.$store.state.signup.form.acceptToBeContacted,
          discountCode: this.$store.state.signup.form.discount_code,
          country: this.$store.state.signup.form.country || 'FR',
        },
        signupError: false,
      };
    },
    validations: {
      form: {
        civility: {
          required,
          civility,
        },
        firstName: {
          required,
          startsWithLetter,
        },
        lastName: {
          required,
          startsWithLetter,
        },
        birthDate: {
          required,
          checkDate,
          over16YearsOld,
        },
        acceptToBeContacted: {
          required,
        },
        country: {
            required,
        },
      },
    },
    methods: {
      async handleSubmit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          return this.$addError(this.$t('signupIdentity.check-form'));
        }

        await this.$store.dispatch('signup/saveForm', { form: this.form });
        await this.$store.dispatch('signup/goToNextStep');
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../assets/grid";

  .row {
    margin-bottom: 2rem;
  }

  .password-input {
    margin-bottom: 0;
  }

  .forgotten-password {
    font-size: 1.4rem;
    color: $fadedTextColor;
  }

  .submit {
    margin-top: 15rem;
    text-align: right;
  }

  .dashed-line {
    margin: 0.5rem 0 3rem;
  }
  .first-name {
    @include onMobile {
      margin-bottom: 2rem;
    }
  }
</style>
